import { VaultItemDTO } from '../types';
import {
  loadWarbandData,
  saveWarbandData,
  StoredVaultItem,
  buildHydrationMaps,
} from './localStorageService';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const hydrateItem = (stored: StoredVaultItem, warbandId: string): VaultItemDTO => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useReferenceDataStore } = require('../stores/referenceDataStore') as typeof import('../stores/referenceDataStore');
  const s = useReferenceDataStore.getState();
  const maps = buildHydrationMaps({
    weapons: s.weapons,
    armours: s.armours,
    buffs: s.buffs,
    treasureItems: s.treasureItems,
    baseResources: s.allBaseResources,
  });
  const item = maps.treasureMap.get(stored.itemId);
  return {
    id: stored.itemId,
    name: item?.name ?? stored.itemId,
    description: item?.description ?? null,
    refreshAfterGame: item?.refreshAfterGame ?? false,
    usesPerGame: item?.usesPerGame ?? 0,
    purchasePrice: item?.purchasePrice ?? null,
    salePrice: item?.salePrice ?? 0,
    ingredientsPrice: item?.ingredientsPrice ?? null,
    invokedSpellId: item?.invokedSpellId ?? null,
    buffId: item?.buffId ?? null,
    baseItemId: item?.baseItemId ?? null,
    baseItemType: item?.baseItemType ?? null,
    instanceId: stored.instanceId,
    warbandId,
    parentItemId: stored.parentItemId,
    warbandMemberId: stored.warbandMemberId,
  };
};

export const vaultService = {
  getVaultItems: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    const items = (data?.vaultItems ?? []).map((v) => hydrateItem(v, warbandId));
    return Promise.resolve({ data: items });
  },

  getVaultItem: (warbandId: string, instanceId: string) => {
    const data = loadWarbandData(warbandId);
    const stored = data?.vaultItems.find((v) => v.instanceId === instanceId);
    return Promise.resolve({ data: stored ? hydrateItem(stored, warbandId) : null });
  },

  addVaultItem: (warbandId: string, itemId: string, parentItemId?: string | null) => {
    const data = loadWarbandData(warbandId);
    if (!data) return Promise.resolve({ data: null as VaultItemDTO | null });
    const newItem: StoredVaultItem = {
      instanceId: crypto.randomUUID(),
      itemId,
      parentItemId: parentItemId ?? null,
      warbandMemberId: null,
    };
    data.vaultItems = [...data.vaultItems, newItem];
    saveWarbandData(warbandId, data);
    return Promise.resolve({ data: hydrateItem(newItem, warbandId) });
  },

  deleteVaultItem: (warbandId: string, instanceId: string | null) => {
    if (!instanceId) return Promise.resolve({ data: undefined });
    const data = loadWarbandData(warbandId);
    if (data) {
      data.vaultItems = data.vaultItems.filter((v) => v.instanceId !== instanceId);
      saveWarbandData(warbandId, data);
    }
    return Promise.resolve({ data: undefined });
  },

  updateVaultItemParentId: (warbandId: string, instanceId: string | null, parentItemId: string | null) => {
    if (!instanceId) return Promise.resolve({ data: null as VaultItemDTO | null });
    const data = loadWarbandData(warbandId);
    if (!data) return Promise.resolve({ data: null as VaultItemDTO | null });
    const idx = data.vaultItems.findIndex((v) => v.instanceId === instanceId);
    if (idx === -1) return Promise.resolve({ data: null as VaultItemDTO | null });
    data.vaultItems[idx] = { ...data.vaultItems[idx], parentItemId };
    saveWarbandData(warbandId, data);
    return Promise.resolve({ data: hydrateItem(data.vaultItems[idx], warbandId) });
  },

  replaceVaultItems: (warbandId: string, items: { itemId: string; parentItemId?: string | null }[]) => {
    const data = loadWarbandData(warbandId);
    if (!data) return Promise.resolve({ data: [] as VaultItemDTO[] });
    const newItems: StoredVaultItem[] = items.map((item) => ({
      instanceId: crypto.randomUUID(),
      itemId: item.itemId,
      parentItemId: item.parentItemId ?? null,
      warbandMemberId: null,
    }));
    data.vaultItems = newItems;
    saveWarbandData(warbandId, data);
    return Promise.resolve({ data: newItems.map((v) => hydrateItem(v, warbandId)) });
  },
};
